import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

// A tab left open keeps running the release it loaded, even after the database
// has moved on (the 26 Oct answer lock left open tabs whose modules wouldn't
// load). Each build names its main script after its contents, so a different
// name in the live index.html means a new release is out.
const MAIN_SCRIPT = /\/static\/js\/(main\.[0-9a-f]+\.js)/;
const CHECK_EVERY_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

const loadedMainScript = (): string | null => {
  const src = document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src') ?? '';
  return src.match(MAIN_SCRIPT)?.[1] ?? null;
};

export const NewVersionBanner: React.FC = () => {
  const { pathname } = useLocation();
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    // The dev server serves an unversioned bundle.js, so there is nothing to compare.
    const loaded = loadedMainScript();
    if (!loaded) return;

    let lastCheck = 0;
    let found = false;
    const check = async () => {
      if (found || document.visibilityState !== 'visible' || Date.now() - lastCheck < MIN_GAP_MS) return;
      lastCheck = Date.now();
      try {
        const response = await fetch('/index.html', { cache: 'no-store' });
        if (!response.ok) return;
        const live = (await response.text()).match(MAIN_SCRIPT)?.[1];
        if (live && live !== loaded) {
          found = true;
          setUpdateReady(true);
        }
      } catch {
        // Offline or mid-deploy: try again on the next check.
      }
    };

    // Every 5 minutes, and when someone comes back to the tab.
    const interval = window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  // Not mid-module or on the result: refreshing there would interrupt a timed
  // attempt. It shows again once they're back on the dashboard.
  if (!updateReady || pathname.startsWith('/quiz/')) return null;

  // Just under the sticky 64px nav bar (HeaderElements), and beneath its z-index
  // so the profile menu still opens over the card. No way to dismiss it: the
  // old release may no longer work with the database, so refreshing is the fix.
  return (
    <div className="fixed inset-x-0 top-[76px] z-[90] flex justify-center px-4 pointer-events-none">
      <div
        role="status"
        className="pointer-events-auto w-full sm:w-auto bg-navy text-white rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 motion-safe:animate-new-version"
      >
        <RefreshCw size={16} className="shrink-0 text-teal" />
        <span className="text-sm flex-1">A new version of the training platform is available.</span>
        <button
          onClick={() => window.location.reload()}
          className="shrink-0 px-3 py-1.5 text-sm font-semibold rounded-md bg-orange hover:bg-orange-hover text-white"
        >
          Refresh
        </button>
      </div>
    </div>
  );
};
